/**
 * Company Growth: one line for the business, priced like a market chart.
 *
 * Every figure comes from GET /dashboard/growth — the analytics service the
 * assistant's context also reads — so the card and the assistant cannot tell
 * an owner two different numbers.
 */

import { useId, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { GROWTH_RANGES, type GrowthRange, type GrowthSummary, type GrowthTotals } from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { LocationScope } from '../../components/LocationScope';
import { count, money } from '../../lib/format';
import { Card, ErrorState, Spinner, cn } from '../../components/ui';

type Metric =
  | 'totalRevenue'
  | 'netLogistics'
  | 'grossDeliveryValue'
  | 'companyKept'
  | 'riderRemittanceRevenue'
  | 'deliveries';

/**
 * Six lines, and six different questions.
 *
 * `Gross delivery value` is what passed through Ceno Hub; `Net logistics` is
 * what it earned on that. A ₦3,000 ride on which the rider keeps ₦2,500 adds
 * ₦3,000 to the first and ₦500 to the second, and only the second is revenue.
 *
 * `Company kept` is fee minus payout — the cash actually held — and sits a
 * hair above net logistics wherever a source sheet's own arithmetic does not
 * close. On the CenoHub book that is one ride and ₦700, which is the point of
 * showing them as separate lines rather than one.
 */
const METRICS: Array<{ key: Metric; label: string; short: string }> = [
  { key: 'totalRevenue', label: 'Total revenue', short: 'Total' },
  { key: 'netLogistics', label: 'Net logistics revenue', short: 'Net logistics' },
  { key: 'grossDeliveryValue', label: 'Gross delivery value', short: 'Gross value' },
  { key: 'companyKept', label: 'Company kept', short: 'Kept' },
  { key: 'riderRemittanceRevenue', label: 'Rider remittance', short: 'Remittance' },
  { key: 'deliveries', label: 'Deliveries', short: 'Deliveries' },
];

const RANGE_LABEL: Record<GrowthRange, string> = {
  '24H': 'last 24 hours',
  '7D': 'last 7 days',
  '1M': 'last 30 days',
  '3M': 'last 90 days',
  YTD: 'year to date',
  '1Y': 'last 12 months',
  MAX: 'all time',
};

const COMPARED_WITH: Record<GrowthRange, string> = {
  '24H': 'vs previous 24 hours',
  '7D': 'vs previous 7 days',
  '1M': 'vs previous 30 days',
  '3M': 'vs previous 90 days',
  YTD: 'vs same period last year',
  '1Y': 'vs previous 12 months',
  MAX: '',
};

const UP = '#16a34a';
const DOWN = '#dc2626';
const FLAT = '#64748b';

export function value(t: GrowthTotals, metric: Metric): number {
  switch (metric) {
    case 'totalRevenue':
      return Number(t.totalRevenue);
    case 'netLogistics':
      return Number(t.deliveryCommission);
    case 'grossDeliveryValue':
      return Number(t.grossDeliveryValue);
    case 'companyKept':
      return Number(t.companyKept);
    case 'riderRemittanceRevenue':
      return Number(t.riderRemittanceRevenue);
    case 'deliveries':
      return t.deliveries;
  }
}

/** Percentage change; null when there was nothing before to compare with. */
export function percentChange(current: number, previous: number | null): number | null {
  if (previous === null || !Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function bucketLabel(bucket: string, grain: GrowthSummary['grain'], long = false): string {
  if (grain === 'hour') {
    const at = new Date(`${bucket}:00`);
    return at.toLocaleString('en-GB', long
      ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' });
  }
  const at = new Date(`${bucket}T00:00:00`);
  if (grain === 'month') {
    return at.toLocaleDateString('en-GB', { month: 'short', year: long ? 'numeric' : '2-digit' });
  }
  const label = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(long ? { year: 'numeric' } : {}) });
  return grain === 'week' && long ? `Week of ${label}` : label;
}

export function GrowthCard(): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const [metric, setMetric] = useState<Metric>('totalRevenue');
  const [range, setRange] = useState<GrowthRange>('1M');
  const gradientId = useId().replace(/:/g, '');

  /*
    The working location, which this card was not sending.

    The endpoint has always taken `branchId` and narrows correctly when given
    one — but the card never passed it, so choosing a location in the header
    changed the rest of the app and left these figures at the business total.
    A filter that silently does nothing is worse than no filter: it answers a
    question the reader thinks they asked.
  */
  const filter = useLocationFilter();
  const locationReady = useLocationReady();

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: ['dashboard-growth', business?.businessId, range, filter],
    queryFn: () => api.get<GrowthSummary>(path(`/dashboard/growth${qs({ range, ...filter })}`)),
    enabled: Boolean(business) && locationReady,
    // Keep the old line on screen while the new range loads, rather than
    // collapsing the card to a spinner on every click.
    placeholderData: keepPreviousData,
  });

  const isMoney = metric !== 'deliveries';
  const format = (v: number) =>
    isMoney ? money(v.toFixed(2), data?.currencyCode ?? '') : count(v);

  const current = data ? value(data.totals, metric) : 0;
  const previous = data?.previousTotals ? value(data.previousTotals, metric) : null;
  const change = percentChange(current, previous);
  const direction = change === null || Math.abs(change) < 0.05 ? 'flat' : change > 0 ? 'up' : 'down';
  const color = direction === 'up' ? UP : direction === 'down' ? DOWN : FLAT;

  const points = (data?.points ?? []).map((p) => ({
    bucket: p.bucket,
    v: value(p, metric),
    p,
  }));

  return (
    <Card className="mt-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-600">Company growth</p>
          <p className="mt-1 text-xs text-slate-500">
            Sales and deliveries, live and imported, counted once
          </p>
          {/*
            Which locations these figures cover, always on screen.

            Shared with the dashboard's own tiles — see components/LocationScope.
            Rider remittance belongs to no location, so the API leaves it out of
            a narrowed total; said here, or the revenue line looks short for no
            reason.
          */}
          <LocationScope
            note="rider remittance not included"
            testId="growth-scope"
            className="mt-1 text-xs text-slate-600"
          />
        </div>

        <div
          role="tablist"
          aria-label="Metric"
          className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1"
        >
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={metric === m.key}
              onClick={() => setMetric(m.key)}
              className={cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                metric === m.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              <span className="sm:hidden">{m.short}</span>
              <span className="hidden sm:inline">{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <Spinner label="Loading growth" />
      ) : error ? (
        <ErrorState message="Could not load company growth." onRetry={() => refetch()} />
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1">
            <p className="text-3xl font-semibold tabular text-slate-900 sm:text-4xl" data-testid="growth-value">
              {format(current)}
            </p>
            {range !== 'MAX' && (
              <p
                className="mb-1 flex items-center gap-1 text-sm font-semibold"
                style={{ color }}
                data-testid="growth-change"
              >
                {direction === 'up' ? (
                  <ArrowUpRight className="size-4" aria-hidden />
                ) : direction === 'down' ? (
                  <ArrowDownRight className="size-4" aria-hidden />
                ) : (
                  <Minus className="size-4" aria-hidden />
                )}
                {change === null ? '—' : `${Math.abs(change).toFixed(1)}%`}
                <span className="font-normal text-slate-500">{COMPARED_WITH[range]}</span>
              </p>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {METRICS.find((m) => m.key === metric)!.label}, {RANGE_LABEL[range]}
            {change === null && range !== 'MAX' && previous === 0 ? ' · nothing in the previous period' : ''}
          </p>

          <div className={cn('mt-4 h-56 sm:h-72', isFetching && 'opacity-60 transition-opacity')}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="bucket"
                  tickFormatter={(b: string) => bucketLabel(b, data.grain)}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={32}
                />
                <YAxis
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                  allowDecimals={isMoney}
                  tickFormatter={(v: number) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(v)}
                />
                <Tooltip
                  cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    const row = active ? (payload?.[0]?.payload as (typeof points)[number] | undefined) : undefined;
                    if (!row) return null;
                    const c = data.currencyCode;
                    return (
                      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
                        <p className="mb-1 font-medium text-slate-900">{bucketLabel(row.bucket, data.grain, true)}</p>
                        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 tabular">
                          <dt className="font-medium text-slate-700">Total revenue</dt>
                          <dd className="text-right font-medium text-slate-900">{money(row.p.totalRevenue, c)}</dd>
                          <dt className="pl-2 text-slate-500">Shop</dt>
                          <dd className="text-right text-slate-900">{money(row.p.shopRevenue, c)}</dd>
                          <dt className="pl-2 text-slate-500">Commission</dt>
                          <dd className="text-right text-slate-900">{money(row.p.deliveryCommission, c)}</dd>
                          <dt className="pl-2 text-slate-500">Remittance</dt>
                          <dd className="text-right text-slate-900">{money(row.p.riderRemittanceRevenue, c)}</dd>
                          {/* Below the rule: throughput, not income. */}
                          <dt className="border-t border-slate-100 pt-1 text-slate-400">Gross delivery fees</dt>
                          <dd className="border-t border-slate-100 pt-1 text-right text-slate-500">{money(row.p.grossDeliveryValue, c)}</dd>
                          <dt className="text-slate-400">Rider payouts</dt>
                          <dd className="text-right text-slate-500">{money(row.p.riderPayouts, c)}</dd>
                          <dt className="text-slate-400">Deliveries</dt>
                          <dd className="text-right text-slate-500">{count(row.p.deliveries)}</dd>
                        </dl>
                      </div>
                    );
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0, fill: color }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <div role="tablist" aria-label="Period" className="mt-3 flex flex-wrap gap-1">
        {GROWTH_RANGES.map((r) => (
          <button
            key={r}
            type="button"
            role="tab"
            aria-selected={range === r}
            onClick={() => setRange(r)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium tabular transition-colors',
              range === r ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100',
            )}
          >
            {r}
          </button>
        ))}
      </div>
    </Card>
  );
}
